/**
 * Literal keyword span for composer skill offers.
 * Compact / slash matches stay on the `/` menu. An offer only exists when a
 * trigger phrase is actually written in the draft, on word boundaries.
 */

const WORD_CHAR = /[\p{L}\p{N}_]/u

export type SkillKeywordSpan = {
  start: number
  end: number
  /** Original casing from the draft. */
  keyword: string
}

function isWordChar(ch: string | undefined): boolean {
  return Boolean(ch) && WORD_CHAR.test(ch as string)
}

/** A hit inside `/pilot-feedback` belongs to the slash menu, not the offer. */
export function isOfferKeywordHit(text: string, start: number, end: number): boolean {
  if (start > 0 && isWordChar(text[start - 1])) return false
  if (end < text.length && isWordChar(text[end])) return false
  let i = start - 1
  while (i >= 0 && !/\s/.test(text[i] ?? '')) {
    if (text[i] === '/') return false
    i -= 1
  }
  return true
}

export function findLiteralSkillKeywordSpan(
  text: string,
  keywords: readonly string[] | undefined,
): SkillKeywordSpan | null {
  if (!text || !keywords?.length) return null
  const candidates = keywords
    .map((keyword) => String(keyword).trim())
    .filter((keyword) => keyword.length > 0 && !keyword.startsWith('/'))
    .sort((a, b) => b.length - a.length)

  const lower = text.toLowerCase()
  for (const keyword of candidates) {
    const needle = keyword.toLowerCase()
    let from = 0
    while (from <= lower.length - needle.length) {
      const start = lower.indexOf(needle, from)
      if (start < 0) break
      const end = start + needle.length
      if (isOfferKeywordHit(text, start, end)) {
        return { start, end, keyword: text.slice(start, end) }
      }
      from = start + 1
    }
  }
  return null
}
